import { useEffect, useId, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Skeleton, Stack, Tooltip } from '@mui/material';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';

interface Props {
  fileName: string;
  load: () => Promise<Blob>;
  canPreview?: boolean;
  disabled?: boolean;
  previewLabel?: string;
}

export default function DocumentActions({ fileName, load, canPreview = true, disabled = false, previewLabel = 'Skoða skjal' }: Props) {
  const titleId = useId();
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const generation = useRef(0);
  useEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const close = () => { generation.current += 1; setPreview(false); setBusy(false); setUrl(''); setError(''); };
  const download = (href: string) => {
    const anchor = document.createElement('a');
    anchor.href = href; anchor.download = fileName;
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
  };
  const fetch = async (view: boolean) => {
    const request = ++generation.current;
    setBusy(true); setError(''); setPreview(view);
    try {
      const blob = await load();
      if (request !== generation.current) return;
      if (view && blob.type.split(';')[0] !== 'application/pdf') throw new Error('Ekki er hægt að forskoða þetta skjal.');
      const objectUrl = URL.createObjectURL(blob);
      if (view) setUrl(objectUrl);
      else {
        download(objectUrl);
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (value) {
      if (request === generation.current) setError(value instanceof Error ? value.message : 'Ekki tókst að sækja skjalið.');
    } finally { if (request === generation.current) setBusy(false); }
  };
  return <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" spacing={0.5} alignItems="center">
      {canPreview ? <>
        <Button size="small" variant="outlined" startIcon={<VisibilityOutlined />} disabled={disabled || busy}
          sx={{ whiteSpace: 'nowrap' }} aria-label={`${previewLabel}: ${fileName}`} onClick={() => void fetch(true)}>{previewLabel}</Button>
        <Tooltip title="Sækja skjal"><span><IconButton size="small" color="primary" disabled={disabled || busy}
          aria-label={`Sækja ${fileName}`} onClick={() => void fetch(false)} sx={{ width: 36, height: 36 }}>
          {busy && !preview ? <CircularProgress size={18} color="inherit" /> : <DownloadOutlined fontSize="small" />}
        </IconButton></span></Tooltip>
      </> : <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlined />}
        disabled={disabled || busy} aria-label={`Sækja ${fileName}`} onClick={() => void fetch(false)}>{busy ? 'Sæki…' : 'Sækja skjal'}</Button>}
    </Stack>
    {error && !preview && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    <Dialog open={preview} onClose={close} maxWidth="lg" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ overflowWrap: 'anywhere' }}>{fileName}</DialogTitle>
      <DialogContent dividers sx={{ height: '75dvh', minHeight: 240, p: 0 }}>
        {error ? <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" onClick={() => void fetch(true)}>Reyna aftur</Button>}>{error}</Alert>
          : busy ? <Box sx={{ p: 3 }}><Skeleton width="55%" height={40} /><Skeleton variant="rectangular" height="55dvh" sx={{ mt: 2 }} /></Box>
            : url && <Box component="iframe" src={url} title={fileName} sx={{ width: '100%', height: '100%', border: 0 }} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button startIcon={<DownloadOutlined />} disabled={!url || busy || !!error} onClick={() => download(url)}>Sækja skjal</Button>
        <Button variant="outlined" onClick={close}>Loka</Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
